import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Box } from "@mui/material";
import { MouseEvent as ReactMouseEvent } from "react";
import {
  DocViewerMoreOptions,
  type MoreOptionsButton,
} from "src/components/DocViewer/components/docViewerTopBar/actions/DocViewerMoreOptions";

import type { DocumentsPageItem } from "../Documents.utils";

type DocumentsTableDocumentItem = Extract<
  DocumentsPageItem,
  { kind: "document" }
>;

export type DocumentsTableRowActionsProps = {
  canDeleteItem: boolean;
  canRenameItem: boolean;
  deleteTooltipTitle?: string;
  disabled?: boolean;
  item: DocumentsPageItem;
  onDeleteStart: (item: DocumentsPageItem) => void;
  onOpenDocumentInNewTab: (item: DocumentsTableDocumentItem) => void;
  onMoveStart: (item: DocumentsPageItem) => void;
  onOpen: (item: DocumentsPageItem) => void;
  onRenameStart: (item: DocumentsPageItem) => void;
  renameTooltipTitle?: string;
};

type DocumentsTableRowActionsMenuPosition = { top: number; left: number };

const stopRowEvent = (event: ReactMouseEvent) => {
  event.stopPropagation();
};

const getItemName = (item: DocumentsPageItem) => item.name.trim() || "No name";

const RowActionsShell = ({
  disabled,
  item,
  normalButtons,
}: {
  disabled: boolean;
  item: DocumentsPageItem;
  normalButtons: MoreOptionsButton[];
}) => (
  <Box
    data-testid="documents-table-row-actions"
    onClick={stopRowEvent}
    onMouseDown={stopRowEvent}
    sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
  >
    <DocViewerMoreOptions
      dataTestId={`documents-table-row-actions-button-${item.kind}-${item.id}`}
      hideTriggerTooltip={false}
      isDisabled={disabled}
      menuId={`documents-table-row-actions-${item.kind}-${item.id}`}
      normalButtons={normalButtons}
      triggerAriaLabel={`More actions for ${getItemName(item)}`}
      triggerIcon={
        <MoreVertIcon fontSize="small" sx={{ color: "text.secondary" }} />
      }
      triggerTooltipTitle="More actions"
    />
  </Box>
);

const RowActionsContextMenu = ({
  anchorPosition,
  disabled,
  item,
  normalButtons,
  onClose,
}: {
  anchorPosition: DocumentsTableRowActionsMenuPosition | null;
  disabled: boolean;
  item: DocumentsPageItem;
  normalButtons: MoreOptionsButton[];
  onClose: () => void;
}) => (
  <DocViewerMoreOptions
    dataTestId={`documents-table-row-context-actions-button-${item.kind}-${item.id}`}
    hideTriggerTooltip={false}
    isDisabled={disabled}
    menuAnchorPosition={anchorPosition}
    menuId={`documents-table-row-context-actions-${item.kind}-${item.id}`}
    normalButtons={normalButtons}
    onMenuClose={onClose}
    renderTrigger={false}
    triggerAriaLabel={`More actions for ${getItemName(item)}`}
  />
);

const getBaseButtons = ({
  canDeleteItem,
  canRenameItem,
  deleteTooltipTitle,
  disabled,
  item,
  onDeleteStart,
  onMoveStart,
  onOpen,
  onRenameStart,
  renameTooltipTitle,
}: DocumentsTableRowActionsProps): MoreOptionsButton[] => [
  {
    title: "Open",
    icon: <OpenInNewOutlinedIcon fontSize="small" />,
    onClick: () => onOpen(item),
  },
  {
    title: "Rename",
    disabled: disabled || !canRenameItem,
    icon: <ModeEditOutlineIcon fontSize="small" />,
    onClick: () => onRenameStart(item),
    tooltipTitle: !canRenameItem ? renameTooltipTitle : undefined,
  },
  {
    title: "Move To",
    disabled,
    icon: <DriveFileMoveOutlinedIcon fontSize="small" />,
    onClick: () => onMoveStart(item),
  },
  {
    title: "Delete",
    disabled: disabled || !canDeleteItem,
    icon: <DeleteOutlinedIcon fontSize="small" />,
    onClick: () => onDeleteStart(item),
    tooltipTitle: !canDeleteItem ? deleteTooltipTitle : undefined,
  },
];

const getDocumentOpenButton = ({
  item,
  onOpenDocumentInNewTab,
}: DocumentsTableRowActionsProps & {
  item: DocumentsTableDocumentItem;
}): MoreOptionsButton => ({
  title: "Open in new tab",
  icon: <OpenInNewOutlinedIcon fontSize="small" />,
  onClick: () => onOpenDocumentInNewTab(item),
});

const DocumentsTableDocumentRowActions = (
  props: DocumentsTableRowActionsProps & { item: DocumentsTableDocumentItem }
) => {
  const [, ...restButtons] = getBaseButtons(props);
  const openInNewTabButton = getDocumentOpenButton(props);

  return (
    <RowActionsShell
      disabled={!!props.disabled}
      item={props.item}
      normalButtons={[openInNewTabButton, ...restButtons]}
    />
  );
};

const DocumentsTableDocumentRowActionsMenu = (
  props: DocumentsTableRowActionsProps & {
    anchorPosition: DocumentsTableRowActionsMenuPosition | null;
    item: DocumentsTableDocumentItem;
    onClose: () => void;
  }
) => {
  const [, ...restButtons] = getBaseButtons(props);
  const openInNewTabButton = getDocumentOpenButton(props);

  return (
    <RowActionsContextMenu
      anchorPosition={props.anchorPosition}
      disabled={!!props.disabled}
      item={props.item}
      normalButtons={[openInNewTabButton, ...restButtons]}
      onClose={props.onClose}
    />
  );
};

export const DocumentsTableRowActions = (
  props: DocumentsTableRowActionsProps
) => {
  if (props.item.kind === "document") {
    return <DocumentsTableDocumentRowActions {...props} item={props.item} />;
  }

  return (
    <RowActionsShell
      disabled={!!props.disabled}
      item={props.item}
      normalButtons={getBaseButtons(props)}
    />
  );
};

export const DocumentsTableRowActionsMenu = (
  props: DocumentsTableRowActionsProps & {
    anchorPosition: DocumentsTableRowActionsMenuPosition | null;
    onClose: () => void;
  }
) => {
  if (props.item.kind === "document") {
    return (
      <DocumentsTableDocumentRowActionsMenu {...props} item={props.item} />
    );
  }

  return (
    <RowActionsContextMenu
      anchorPosition={props.anchorPosition}
      disabled={!!props.disabled}
      item={props.item}
      normalButtons={getBaseButtons(props)}
      onClose={props.onClose}
    />
  );
};
